/**
 * Make a score config, top to bottom: its name, what reviewers answer (three
 * cards: pass / fail, a scale, categories), the settings of that answer (a
 * scale's points and end labels, the categories one per row), a guideline,
 * and a live "Reviewers see" preview drawn with the queue page's own control.
 * Used on the Scores tab (in a dialog) and inside "New queue" (inline, the new
 * score ticked). Configs cannot be edited later, so the form says so.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { ScoreStep } from "./annotation-form";
import { ChoiceList } from "./choice-list";
import { useCreateScoreConfig } from "./hooks";
import { KindCards, Points } from "./score-pickers";
import type { ScoreConfig, ScoreKind } from "./types";

const ABOUT: Record<ScoreKind, string> = {
  pass_fail: "Pass counts as 1 and fail as 0, so averages read as a pass rate.",
  scale: "Averaged like any number.",
  categories: "Counted per choice, not averaged.",
};

/** The draft as a config, for the preview (and nothing else). */
function draftConfig(d: {
  name: string;
  kind: ScoreKind;
  description: string;
  max: number;
  low: string;
  high: string;
  categories: string[];
}): ScoreConfig {
  return {
    id: "preview",
    name: d.name.trim() || "Your score",
    kind: d.kind,
    description: d.description.trim(),
    categories: d.categories.map((c) => c.trim()).filter(Boolean),
    scale_min: 1,
    scale_max: d.max,
    scale_labels: d.low || d.high ? [d.low, d.high] : [],
    created_by: "",
    created_at: "",
  };
}

export function ScoreConfigForm({
  onCreated,
  onCancel,
}: {
  onCreated: (config: ScoreConfig) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ScoreKind>("pass_fail");
  const [description, setDescription] = useState("");
  const [max, setMax] = useState(5);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [categories, setCategories] = useState(["", ""]);
  const [tried, setTried] = useState("");
  const create = useCreateScoreConfig();
  const notify = useUiStore((s) => s.notify);
  const draft = draftConfig({ name, kind, description, max, low, high, categories });
  const ready = name.trim() && (kind !== "categories" || draft.categories.length >= 2);

  const save = () =>
    create.mutate(
      {
        name: name.trim(),
        kind,
        description: description.trim(),
        ...(kind === "categories" ? { categories: draft.categories } : {}),
        ...(kind === "scale" ? { scale_min: 1, scale_max: max, scale_labels: draft.scale_labels } : {}),
      },
      {
        onSuccess: (c) => (notify(`Created ${c.name}.`), onCreated(c)),
        onError: (e) => notify(errorText(e, "Could not create the score."), "error"),
      },
    );

  return (
    <div className="flex flex-col gap-4">
      <Field label="Score name" required htmlFor="score-name" hint="The column name on the experiment page.">
        <input
          id="score-name"
          className="inp"
          placeholder="e.g. Decision correct"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="What do reviewers answer?" hint={ABOUT[kind]}>
        <KindCards value={kind} onChange={(k) => (setKind(k), setTried(""))} />
      </Field>
      {kind === "scale" && (
        <div className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-semibold">Points</span>
            <Points value={max} onChange={setMax} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="What 1 means" optional htmlFor="scale-low">
              <input
                id="scale-low"
                className="inp"
                placeholder="e.g. Wrong or missing"
                value={low}
                onChange={(e) => setLow(e.target.value)}
              />
            </Field>
            <Field label={`What ${max} means`} optional htmlFor="scale-high">
              <input
                id="scale-high"
                className="inp"
                placeholder="e.g. Complete and accurate"
                value={high}
                onChange={(e) => setHigh(e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}
      {kind === "categories" && (
        <Field label="Choices" required hint="At least two. Enter adds the next one.">
          <ChoiceList value={categories} onChange={setCategories} />
        </Field>
      )}
      <Field
        label="Guideline"
        optional
        htmlFor="score-description"
        hint="Shown to reviewers under the score."
      >
        <textarea
          id="score-description"
          className="inp min-h-[56px]!"
          placeholder="What to look for, e.g. check the decision against the screening criteria"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <section
        aria-label="Preview"
        className="flex flex-col gap-2 rounded-lg border border-dashed border-line p-3"
      >
        <span className="text-[11px] font-semibold tracking-wide text-faint uppercase">Reviewers see</span>
        <ScoreStep step={1} score={draft} value={tried} onChange={setTried} />
      </section>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-faint">A score cannot be changed once made.</span>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" disabled={!ready || create.isPending} onClick={save}>
          Create score
        </Button>
      </div>
    </div>
  );
}

export function ScoreConfigDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog
      title="New score"
      subtitle="Something people score by hand. Any queue can ask it."
      onClose={onClose}
      width={600}
    >
      <ScoreConfigForm onCreated={onClose} onCancel={onClose} />
    </Dialog>
  );
}
